import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/molecules/form-field';
import type { Country } from '@/lib/types';
import type { CreateShopFormData, UpdateShopFormData } from '../types/shop-form.types';

interface ShopLocationProps<T extends CreateShopFormData | UpdateShopFormData> {
  form: UseFormReturn<T>;
  countries: Country[];
}

export function ShopLocation<T extends CreateShopFormData | UpdateShopFormData>({
  form,
  countries,
}: ShopLocationProps<T>) {
  const { t } = useTranslation();
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const formValues = form.watch();

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t('shops.geolocationNotSupported'));
      return;
    }

    setIsGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        (form.setValue as any)('latitude', position.coords.latitude);
        (form.setValue as any)('longitude', position.coords.longitude);
        toast.success(t('shops.locationObtained'));
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error(t('shops.locationPermissionDenied'));
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error(t('shops.locationUnavailable'));
            break;
          case error.TIMEOUT:
            toast.error(t('shops.locationTimeout'));
            break;
          default:
            toast.error(t('shops.locationError'));
        }
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('shops.addressTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name={'countryIsoCode3' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shops.country')} *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('shops.selectCountry')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.isoCode3} value={country.isoCode3}>
                      {country.flagEmoji} {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={'city' as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('shops.city')} *</FormLabel>
                <FormControl>
                  <Input placeholder={t('shops.cityPlaceholder')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'department' as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('shops.department')}</FormLabel>
                <FormControl>
                  <Input placeholder={t('shops.departmentPlaceholder')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name={'address' as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shops.address')} *</FormLabel>
              <FormControl>
                <Input placeholder={t('shops.addressPlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'postalCode' as any}
          render={({ field }) => (
            <FormItem className="max-w-[200px]">
              <FormLabel>{t('shops.postalCode')}</FormLabel>
              <FormControl>
                <Input placeholder="75001" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* GPS Coordinates */}
        <div className="pt-4 border-t">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <MapPin className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">{t('shops.gpsCoordinates')}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGetLocation}
              disabled={isGettingLocation}
              className="w-full sm:w-auto"
            >
              {isGettingLocation ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <Navigation className="size-4 mr-2" />
              )}
              {t('shops.useMyLocation')}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name={'latitude' as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('shops.latitude')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any"
                      placeholder="-11.7172"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.valueAsNumber;
                        field.onChange(isNaN(val) ? undefined : val);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'longitude' as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('shops.longitude')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any"
                      placeholder="43.2551"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.valueAsNumber;
                        field.onChange(isNaN(val) ? undefined : val);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {formValues.latitude && formValues.longitude && (
            <p className="text-xs text-muted-foreground mt-2">
              {t('shops.coordinatesSet')}: {(formValues.latitude as number).toFixed(6)}, {(formValues.longitude as number).toFixed(6)}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
